import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Dropdown, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { Add, Copy, Trash } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import LinkAcquisto from '../../../components/LinkAcquisto/LinkAcquisto';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { sidSelectStyles } from '../../../components/Util/sidSelectStyles';
import { usePageTitle } from '../../../layouts/PageHeaderExtra';
import { SelettoreStato, useStatoNelTitolo } from '../../../components/Util/StatoProdotto';
import MyApiClient from '../../../../api-client/MyApiClient';
import { SidPagination } from '../../../components/Tabella/SidPagination';
import { useMaestriEImpianti } from './datiReali';
import { sports } from '../../../components/Util/enum';
import { VAT_OPTIONS, findVatOption, vatFieldsFromOption } from '../../../components/Util/vatOptions';
import ProductImageUploader from '../../../components/Util/ProductImageUploader';
import { PagamentoAcquisto } from '../../../components/Vendite/formePagamento';
import {
    aggiungiSedute,
    assegnaPartecipanti,
    cambiaStatoSquadra,
    controllaDateSulServer,
    creaSquadra,
    eliminaCorso,
    eliminaSeduta,
    eliminaSquadra,
    riallineaDaSeduta,
    salvaCorso,
    salvaSquadra,
    spostaSeduta,
    togliDalCorso,
    useCorso,
} from './datiCorso';
import {
    GIORNI,
    conflittoDi,
    dataBreve,
    dateDelCorso,
    distinti,
    generaDateRegola,
    nomeGiorno,
    riepilogoDate,
} from './pianificazione';

/*
 * Corsi — la gestione di un corso con piu' squadre, tutta dentro la scheda del corso: le squadre
 * (con posti liberi e conflitti), la formazione delle squadre (assegnazione in blocco) e la
 * pianificazione delle sedute.
 *
 * Tutto quello che si vede arriva dal gestionale e tutto quello che si cambia ci torna: la
 * squadra si crea e si attiva, e da li' le sedute vivono a calendario, una per una. Dopo ogni
 * scrittura il corso si rilegge, cosi' la pagina non racconta mai una cosa diversa dal server.
 */

const STATI_SQUADRA = ['Attiva', 'Non attiva', 'Bozza'];

const COLORI = ['#2563EB', '#059669', '#B45309', '#7C3AED', '#BE123C', '#0891B2'];
const coloreSquadra = (squadre, id) => COLORI[Math.max(0, squadre.findIndex(s => s.id === id)) % COLORI.length];

const nuovoId = (prefisso) => `${prefisso}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

// Il motivo che arriva dal server spesso è già una frase compiuta: aggiungere il punto
// a occhi chiusi lasciava messaggi che finivano con «..».
const frase = (testo) => (/[.!?…]\s*$/.test(String(testo ?? '')) ? String(testo).trim() : `${String(testo ?? '').trim()}.`);

const squadraVuota = (corso) => ({
    id: nuovoId('s'),
    nome: '',
    maestroId: '',
    impiantoId: '',
    // I posti partono da quelli del corso: una squadra piu' capiente del corso il
    // gestionale non la accetta, e non ha senso proporla.
    maxMembri: corso.maxPartecipanti || 12,
    dal: corso.dal,
    al: corso.al,
    stato: 'Bozza',
    regola: [],
    membri: [],
    date: [],
});

export const CorsiCorsoPage = () => {
    const { maestri, impianti, vuoto: mancanoRisorse } = useMaestriEImpianti();
    // I nomi di maestri e impianti: si rifanno solo quando cambiano i loro elenchi, altrimenti
    // ogni render rifarebbe i conflitti di tutte le date.
    const nomi = useMemo(() => ({
        maestro: (id) => maestri.find(m => m.id === id)?.nome || '—',
        impianto: (id) => impianti.find(v => v.id === id)?.nome || '—',
    }), [maestri, impianti]);
    const nomeM = nomi.maestro;
    const nomeI = nomi.impianto;
    // La squadra si apre in una pagina sua, dentro il TAB Squadre: ha un indirizzo proprio
    // (/struttura/corsi/k1/squadre/s1), si può ricaricare e mandare a un collega.
    const { id, squadraId } = useParams();
    const navigate = useNavigate();
    const { corso: c, caricando, errore, ricarica } = useCorso(id);

    const [squadraAperta, setSquadraAperta] = useState(null); // copia di lavoro della squadra aperta
    const [selezionati, setSelezionati] = useState([]);
    const [filtro, setFiltro] = useState('tutti');
    const [ricerca, setRicerca] = useState('');
    const [pagina, setPagina] = useState(0);
    const [avviso, setAvviso] = useState(null);
    const [avvisoAssegna, setAvvisoAssegna] = useState(null); // accanto alla barra delle azioni sui selezionati
    const [daEliminare, setDaEliminare] = useState(null);
    const [corsoDaEliminare, setCorsoDaEliminare] = useState(false);
    const [daRimuovere, setDaRimuovere] = useState(null);
    const [datiCorso, setDatiCorso] = useState(null); // modifiche non ancora salvate della scheda
    const [salvato, setSalvato] = useState(false);
    const [creazione, setCreazione] = useState(null); // squadra in attesa di conferma per la creazione
    const [lavorazione, setLavorazione] = useState(null);
    const [tabAttivo, setTabAttivo] = useState(squadraId ? 'squadre' : 'informazioni');

    usePageTitle(c ? c.nome : null);

    // Stato dalla chip accanto al titolo: Attivo <-> Non attivo si registra subito e si resta nella scheda.
    // Una bozza mostra «Bozza» e si pubblica dal campo Stato della scheda.
    const cambiaStato = async (attiva) => {
        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (!tokenDetailsString) return { annullato: true };
        const token = JSON.parse(tokenDetailsString).idToken;
        const risposta = await new MyApiClient().changeCourseStatus(token, id, attiva ? 'PURCHASABLE' : 'CANCELLED');
        if (risposta?.error) return { error: risposta.error };
        await ricarica();
        return {};
    };
    useStatoNelTitolo({
        visibile: !!c,
        attivo: c?.stato === 'Attivo',
        bozza: c?.stato === 'Bozza',
        onCambia: cambiaStato,
    });

    // La squadra aperta la decide l'indirizzo
    useEffect(() => {
        if (!c) return;
        if (!squadraId) { setSquadraAperta(null); return; }
        setTabAttivo('squadre');
        if (squadraId === 'nuova') {
            // se arrivo da «Duplica» la copia è già pronta, altrimenti parto da una squadra vuota
            setSquadraAperta((prec) => (prec && !c.squadre.some(x => x.id === prec.id) ? prec : squadraVuota(c)));
            return;
        }
        const s = c.squadre.find(x => x.id === squadraId);
        setSquadraAperta(s ? { ...s, date: (s.date || []).map(d => ({ ...d })) } : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [squadraId, id, c]);

    const postiOccupati = useMemo(() => {
        const mappa = {};
        (c?.partecipanti || []).forEach(p => { if (p.squadraId) mappa[p.squadraId] = (mappa[p.squadraId] || 0) + 1; });
        return mappa;
    }, [c]);

    if (!c) {
        return (
            <Card><Card.Body className="sid-card-body--spaced">
                {caricando ? 'Caricamento del corso…' : `Corso non disponibile${errore ? `: ${errore}` : '.'}`}
                <Button variant="link" onClick={() => navigate('/struttura/corsi')}>Torna all&apos;elenco</Button>
            </Card.Body></Card>
        );
    }

    const daAssegnare = c.partecipanti.filter(p => !p.squadraId);
    const daPagare = c.partecipanti.filter(p => p.pagato === false);
    const liberi = (squadra) => (parseInt(squadra.maxMembri, 10) || 0) - (postiOccupati[squadra.id] || 0);

    const cercato = ricerca.trim().toLowerCase();
    const partecipantiFiltrati = c.partecipanti
        .filter(p => (filtro === 'tutti' ? true : filtro === 'assegnati' ? !!p.squadraId : !p.squadraId))
        .filter(p => !cercato || [p.nome, p.email, p.telefono].some(v => (v || '').toLowerCase().includes(cercato)));
    const PER_PAGINA = 10;
    const pagine = Math.max(1, Math.ceil(partecipantiFiltrati.length / PER_PAGINA));
    const pagineDi = partecipantiFiltrati.slice(Math.min(pagina, pagine - 1) * PER_PAGINA, (Math.min(pagina, pagine - 1) + 1) * PER_PAGINA);

    /** Una scrittura sul gestionale, con l'attesa davanti e il corso riletto alla fine. */
    const conAttesa = async (passoIniziale, lavoro) => {
        setLavorazione({ passo: passoIniziale });
        try {
            const esito = await lavoro((passo) => setLavorazione({ passo }));
            await ricarica();
            setLavorazione(esito ? { fatto: true, ...esito } : null);
            return esito;
        } catch (e) {
            console.error('Corsi: operazione non riuscita', e);
            await ricarica();
            setLavorazione({ fatto: true, errore: true, testo: `Operazione non riuscita: ${frase(e?.message || 'errore del server')}` });
            return { errore: true };
        }
    };

    const confermaCreazione = async () => {
        const squadra = creazione;
        setCreazione(null);
        await conAttesa('Creazione della squadra…', async (passo) => {
            const esito = await creaSquadra(c.id, squadra, passo);
            if (esito.bozza) {
                return { testo: `Squadra «${squadra.nome}» salvata come bozza: le sedute nasceranno quando la attivi.` };
            }
            // Se la squadra e' stata creata e si e' fermata dopo, dirlo: altrimenti si legge
            // «creazione non riuscita» davanti a una squadra che invece e' in elenco.
            if (esito.errore) {
                return esito.idSquadra
                    ? { errore: true, testo: `La squadra «${squadra.nome}» è stata creata come bozza, ma non è stato possibile attivarla: ${frase(esito.errore)} La trovi in elenco: correggi e riprova ad attivarla.` }
                    : { errore: true, testo: `Creazione non riuscita: ${frase(esito.errore)}` };
            }
            return {
                testo: `Squadra «${squadra.nome}» creata e attivata: le sedute le ha generate il gestionale`
                    + (esito.eccezioni > 0
                        ? `; ${esito.spostate} eccezioni applicate${esito.fallite > 0 ? `, ${esito.fallite} non riuscite` : ''}.`
                        : '.'),
            };
        });
        navigate(`/struttura/corsi/${c.id}`);
    };

    const tornaAllElenco = () => { setAvviso(null); navigate(`/struttura/corsi/${c.id}`); };
    const apriNuova = () => { setAvviso(null); navigate(`/struttura/corsi/${c.id}/squadre/nuova`); };
    const apriModifica = (s) => { setAvviso(null); navigate(`/struttura/corsi/${c.id}/squadre/${s.id}`); };
    const duplica = (s) => {
        setAvviso(null);
        setSquadraAperta({
            ...s,
            id: nuovoId('s'),
            nome: `${s.nome} (copia)`,
            stato: 'Bozza',
            membri: [],
            date: (s.date || []).map(d => ({ ...d, id: nuovoId('d') })),
        });
        navigate(`/struttura/corsi/${c.id}/squadre/nuova`);
    };

    const esistente = (s) => c.squadre.some(x => x.id === s?.id);

    /**
     * Salva la squadra. Se è nuova si crea davvero sul gestionale (e da lì il server genera le
     * sedute), se c'era già si aggiornano nome, posti e stato: giorni e orari si cambiano dalla
     * pianificazione, una seduta alla volta.
     */
    const salva = async () => {
        const s = squadraAperta;
        if (!s.nome.trim()) return setAvviso({ tipo: 'danger', testo: 'Dai un nome alla squadra.' });
        if (!s.dal || !s.al) return setAvviso({ tipo: 'danger', testo: 'Indica il periodo della squadra (dal e al).' });

        // Attivare vuol dire mettere le sedute a calendario: senza pianificazione non c'e'
        // niente da mettere, e il gestionale rifiuterebbe.
        if (s.stato === 'Attiva' && (s.date || []).length === 0) {
            return setAvviso({ tipo: 'danger', testo: 'Genera la pianificazione prima di attivare la squadra: scegli giorno, orario, maestro e impianto e premi «Genera».' });
        }

        // In bozza la squadra si puo' parcheggiare anche senza pianificazione: si torna a
        // finirla dopo. E' l'attivazione a pretenderla, ed e' il controllo qui sopra.
        if (!esistente(s)) {
            setAvviso(null);
            setCreazione(s);
            return;
        }

        setAvviso(null);
        // Finche' la squadra e' rimasta in bozza il periodo si puo' ancora cambiare: non
        // ci sono sedute a calendario da tenere allineate.
        const eraBozza = c.squadre.find(x => x.id === s.id)?.stato === 'Bozza';
        const esito = await conAttesa('Salvataggio della squadra…', async () => {
            const problema = await salvaSquadra(s, eraBozza);
            return problema ? { errore: true, testo: `Salvataggio non riuscito: ${frase(problema)}` } : null;
        });
        if (!esito) navigate(`/struttura/corsi/${c.id}`);
    };

    const confermaEliminaSquadra = async () => {
        const squadra = daEliminare;
        setDaEliminare(null);
        if (!esistente(squadra)) { tornaAllElenco(); return; }
        const esito = await conAttesa('Eliminazione della squadra…', async () => {
            const problema = await eliminaSquadra(squadra.id);
            return problema ? { errore: true, testo: `Eliminazione non riuscita: ${frase(problema)}` } : null;
        });
        if (!esito) tornaAllElenco();
    };

    const assegnaA = async (idSquadra) => {
        const squadra = c.squadre.find(s => s.id === idSquadra);
        const scelti = c.partecipanti.filter(p => selezionati.includes(p.id));
        const daSpostare = scelti.filter(p => p.squadraId !== idSquadra).length;
        if (squadra && daSpostare > liberi(squadra)) {
            setAvvisoAssegna(liberi(squadra) <= 0
                ? `«${squadra.nome}» è al completo: non ci sono posti liberi.`
                : `In «${squadra.nome}» ${liberi(squadra) === 1 ? "c'è 1 posto libero" : `ci sono ${liberi(squadra)} posti liberi`} e ne hai scelti ${daSpostare}.`);
            return;
        }
        setAvvisoAssegna(null);
        setAvviso(null);
        await conAttesa(idSquadra ? 'Assegnazione in corso…' : 'Rimozione dalla squadra…', async () => {
            const problemi = await assegnaPartecipanti(scelti, idSquadra);
            return problemi.length > 0
                ? { errore: true, testo: `Qualcosa non è andato: ${problemi.join('; ')}.` }
                : null;
        });
        setSelezionati([]);
    };

    const confermaRimozione = async () => {
        const scelti = c.partecipanti.filter(p => daRimuovere.includes(p.id));
        setDaRimuovere(null);
        await conAttesa('Rimozione dal corso…', async () => {
            const problemi = await togliDalCorso(scelti);
            return problemi.length > 0 ? { errore: true, testo: `Qualcosa non è andato: ${problemi.join('; ')}.` } : null;
        });
        setSelezionati([]);
    };

    /**
     * Elimina il corso. Il server lo permette in qualsiasi stato ma solo finche' nessuno si e'
     * iscritto o ha pagato: se rifiuta, il suo motivo si mostra tale e quale, perche' dice anche
     * cosa fare al posto dell'eliminazione.
     */
    const confermaEliminaCorso = async () => {
        setCorsoDaEliminare(false);
        const esito = await conAttesa('Eliminazione del corso…', async () => {
            const problema = await eliminaCorso(id);
            return problema ? { errore: true, testo: `Eliminazione non riuscita: ${frase(problema)}` } : null;
        });
        if (!esito) navigate('/struttura/corsi');
    };

    const spunta = (pid) => setSelezionati(prev => (prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]));

    return (
        <>
            <Modal show={!!daEliminare} centered onHide={() => setDaEliminare(null)}>
                <Modal.Header><Modal.Title>Sei sicuro?</Modal.Title></Modal.Header>
                <Modal.Body>
                    La squadra «{daEliminare?.nome}» viene eliminata. I suoi {postiOccupati[daEliminare?.id] || 0} iscritti tornano fra quelli da assegnare.
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button variant="light" onClick={() => setDaEliminare(null)}>Chiudi</Button>
                    <Button variant="danger" onClick={confermaEliminaSquadra}>Elimina</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={corsoDaEliminare} centered onHide={() => setCorsoDaEliminare(false)}>
                <Modal.Header><Modal.Title>Sei sicuro?</Modal.Title></Modal.Header>
                <Modal.Body>
                    Il corso «{c?.nome}» viene eliminato definitivamente, con le sue squadre, le sedute
                    già a calendario e gli ordini non ancora pagati. Non è possibile eliminarlo se ha
                    acquisti pagati o presenze registrate: in quel caso puoi impostarlo come Non attivo.
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button variant="light" onClick={() => setCorsoDaEliminare(false)}>Chiudi</Button>
                    <Button variant="danger" onClick={confermaEliminaCorso}>Elimina il corso</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!creazione} centered onHide={() => setCreazione(null)}>
                <Modal.Header><Modal.Title>Creare la squadra sul gestionale?</Modal.Title></Modal.Header>
                <Modal.Body>
                    {creazione?.stato === 'Attiva' ? (
                        <>
                            <p>
                                La squadra «{creazione?.nome}» viene creata nel corso con i suoi giorni e orari, e attivata:
                                da lì il gestionale genera tutte le sedute dal {dataBreve(creazione?.dal)} al {dataBreve(creazione?.al)}.
                            </p>
                            <p className="mb-0 text-muted">
                                Le date che si discostano dalla regola settimanale vengono poi spostate una per una.
                            </p>
                        </>
                    ) : (
                        <p className="mb-0">
                            La squadra «{creazione?.nome}» viene salvata come bozza, con i giorni e gli orari che hai
                            scelto. Le sedute non vanno ancora a calendario: nascono quando la attivi.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button variant="light" onClick={() => setCreazione(null)}>Annulla</Button>
                    <Button variant="primary" onClick={confermaCreazione}>Crea la squadra</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!lavorazione} centered onHide={() => lavorazione?.fatto && setLavorazione(null)}>
                <Modal.Body className="text-center py-4">
                    {lavorazione?.fatto ? (
                        <div className={lavorazione.errore ? 'text-danger' : ''}>{lavorazione.testo}</div>
                    ) : (
                        <>
                            <Spinner animation="border" role="status"><span className="sr-only">Attendere…</span></Spinner>
                            <div className="mt-2">{lavorazione?.passo}</div>
                        </>
                    )}
                </Modal.Body>
                {lavorazione?.fatto && (
                    <Modal.Footer className="justify-content-end">
                        <Button variant="primary" onClick={() => setLavorazione(null)}>Chiudi</Button>
                    </Modal.Footer>
                )}
            </Modal>

            <Modal show={!!daRimuovere} centered onHide={() => setDaRimuovere(null)}>
                <Modal.Header><Modal.Title>Sei sicuro?</Modal.Title></Modal.Header>
                <Modal.Body>
                    {daRimuovere?.length === 1
                        ? "Il partecipante esce dalla sua squadra e il suo acquisto viene annullato."
                        : `I ${daRimuovere?.length} partecipanti scelti escono dalle loro squadre e i loro acquisti vengono annullati.`}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button variant="light" onClick={() => setDaRimuovere(null)}>Chiudi</Button>
                    <Button variant="danger" onClick={confermaRimozione}>Rimuovi dal corso</Button>
                </Modal.Footer>
            </Modal>

            <Row className="g-3 mb-3">
                <Col md={4}><Riquadro etichetta="Iscritti al corso" valore={c.partecipanti.length} /></Col>
                <Col md={4}><Riquadro etichetta="Da assegnare" valore={daAssegnare.length} allarme={daAssegnare.length > 0} /></Col>
                <Col md={4}><Riquadro etichetta="Partecipanti non pagati" valore={daPagare.length} allarme={daPagare.length > 0} /></Col>
            </Row>

            {mancanoRisorse && (
                <Alert variant="warning" className="text-dark">
                    Maestri o impianti della struttura non disponibili: senza quelli non si può pianificare una squadra.
                </Alert>
            )}

            {avviso && (
                <Alert variant={avviso.tipo} className="text-dark">
                    <strong>{avviso.testo}</strong>
                    {avviso.elenco && <ul className="mb-0 mt-2">{avviso.elenco.map((r, i) => <li key={i}>{r}</li>)}</ul>}
                </Alert>
            )}

            <div className="custom-tab-1">
                <Tab.Container activeKey={tabAttivo} onSelect={(k) => { if (squadraId) tornaAllElenco(); setTabAttivo(k || 'informazioni'); }}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                            <Nav.Item as="li" className="tabMenu"><Nav.Link eventKey="informazioni">Informazioni generali</Nav.Link></Nav.Item>
                            <Nav.Item as="li" className="tabMenu"><Nav.Link eventKey="partecipanti">Partecipanti</Nav.Link></Nav.Item>
                            <Nav.Item as="li" className="tabMenu"><Nav.Link eventKey="squadre">Squadre e programmazione</Nav.Link></Nav.Item>
                        </Nav>
                        {/* Link pubblico per vendere il corso via email o WhatsApp */}
                        <LinkAcquisto productType="course" productId={id} nomeProdotto={c.nome} attivo={c.stato === 'Attivo'} />
                    </div>
                    <Tab.Content className="mt-4">
                        {/* ── Informazioni generali ─────────────────── */}
                        <Tab.Pane eventKey="informazioni">
                            <InformazioniCorso dati={datiCorso || c} onCambia={(d) => { setDatiCorso(d); setSalvato(false); }} />
                        </Tab.Pane>

                        {/* ── Squadre ───────────────────────────────── */}
                        <Tab.Pane eventKey="squadre">
                            {squadraAperta ? (
                                /* La squadra aperta prende il posto dell'elenco, dentro questo TAB. */
                                <PannelloSquadra
                                    squadra={squadraAperta}
                                    dateAltreSquadre={dateDelCorso(c, squadraAperta.id)}
                                    esistente={esistente(squadraAperta)}
                                    maxCorso={c.maxPartecipanti}
                                    sport={c.sportType}
                                    corsoAttivo={c.stato === 'Attivo'}
                                    maestri={maestri}
                                    impianti={impianti}
                                    nomi={nomi}
                                    onCambia={setSquadraAperta}
                                    onSalva={salva}
                                    onAnnulla={tornaAllElenco}
                                    onDuplica={() => duplica(squadraAperta)}
                                    onElimina={() => setDaEliminare(squadraAperta)}
                                    conAttesa={conAttesa}
                                    onStatoCambiato={ricarica}
                                />
                            ) : (
                                <Card>
                                    <Card.Header className="sid-list-header">
                                        <div className="sid-list-header__top">
                                            <h4 className="card-title">Squadre</h4>
                                            <div className="sid-list-header__actions">
                                                {c.stato === 'Attivo' ? (
                                                    <Button className="btn btn-primary btn-sm btn-add" onClick={apriNuova}>
                                                        <Add variant="Bulk" /><span className="ms-2">Nuova squadra</span>
                                                    </Button>
                                                ) : (
                                                    <span className="text-muted small">Le squadre si creano quando il corso è attivo.</span>
                                                )}
                                            </div>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="sid-card-body--spaced">
                                        {c.squadre.length === 0 ? (
                                            <p className="text-muted mb-3">{c.stato === 'Attivo' ? 'Nessuna squadra: creane una per dare al corso giorni, orari, maestro e impianto.' : 'Nessuna squadra: attiva il corso per crearne una.'}</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table dataTable display">
                                                    <thead>
                                                        <tr>
                                                            <th>Squadra</th>
                                                            <th>Giorni e orari</th>
                                                            <th>Maestro</th>
                                                            <th>Impianto</th>
                                                            <th>Posti</th>
                                                            <th>Periodo</th>
                                                            <th>Stato</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {c.squadre.map(s => {
                                                            const pieni = liberi(s) <= 0;
                                                            return (
                                                                <tr key={s.id} className="sid-row-link" role="button" tabIndex={0}
                                                                    onClick={() => apriModifica(s)}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter') apriModifica(s); }}>
                                                                    <td className="sid-row-link__name">
                                                                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: coloreSquadra(c.squadre, s.id), marginRight: 8 }} />
                                                                        {s.nome}
                                                                    </td>
                                                                    <td>{riepilogoDate(s.date)}</td>
                                                                    <td>{distinti(s.date, 'maestroId', nomeM)}</td>
                                                                    <td>{distinti(s.date, 'impiantoId', nomeI)}</td>
                                                                    <td className={pieni ? 'text-danger fw-bold' : ''}>{postiOccupati[s.id] || 0} / {s.maxMembri}</td>
                                                                    <td>{dataBreve(s.dal)} – {dataBreve(s.al)}</td>
                                                                    <td><ChipStato stato={s.stato} /></td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}
                        </Tab.Pane>

                        {/* ── Partecipanti ──────────────────────────── */}
                        <Tab.Pane eventKey="partecipanti">
                            <Card>
                                <Card.Body className="sid-card-body--spaced">
                                    {/* Ricerca e filtri sulla stessa riga, come nelle liste dei ticket;
                                        le azioni di gruppo compaiono sotto solo quando si seleziona qualcuno. */}
                                    <div className="d-flex justify-content-end mb-3">
                                        <Button className="btn btn-primary btn-sm btn-add" onClick={() => navigate(`/struttura/corsi/${c.id}/partecipanti/nuovo`)}>
                                            <Add variant="Bulk" /><span className="ms-2">Nuovo partecipante</span>
                                        </Button>
                                    </div>
                                    <div className="sid-list-header__bottom d-flex flex-wrap align-items-end gap-3">
                                        <div style={{ minWidth: 260, flex: '0 1 320px' }}>
                                            <Form.Label className="mb-2">Cerca per nome, email o telefono</Form.Label>
                                            <Form.Control className="mt-2" value={ricerca} onChange={(e) => { setRicerca(e.target.value); setPagina(0); }} />
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {[
                                                { id: 'tutti', etichetta: 'Tutti', quanti: c.partecipanti.length },
                                                { id: 'da-assegnare', etichetta: 'Da assegnare', quanti: daAssegnare.length },
                                                { id: 'assegnati', etichetta: 'Assegnati', quanti: c.partecipanti.length - daAssegnare.length },
                                            ].map(f => (
                                                <button key={f.id} type="button"
                                                    className={`sid-chip${filtro === f.id ? ' is-active' : ''}`}
                                                    onClick={() => { setFiltro(f.id); setSelezionati([]); setPagina(0); }}>
                                                    {f.etichetta} ({f.quanti})
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {selezionati.length > 0 && (
                                        <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                                            <span className="sid-list-total">{selezionati.length === 1 ? '1 selezionato' : `${selezionati.length} selezionati`}</span>
                                            <button type="button" className="sid-chip" onClick={() => { setSelezionati([]); setAvvisoAssegna(null); }}>Deseleziona</button>
                                            <Dropdown>
                                                <Dropdown.Toggle as="button" type="button" className="sid-chip">Assegna a squadra</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    {c.squadre.map(sq => (
                                                        <Dropdown.Item key={sq.id} onClick={() => assegnaA(sq.id)}>
                                                            {sq.nome} <span className="text-muted">· {liberi(sq) > 0 ? `${liberi(sq)} liberi` : 'al completo'}</span>
                                                        </Dropdown.Item>
                                                    ))}
                                                    {c.squadre.length === 0 && <Dropdown.Item disabled>Nessuna squadra</Dropdown.Item>}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            <button type="button" className="sid-chip" onClick={() => assegnaA(null)}>Togli dalla squadra</button>
                                            <button type="button" className="sid-chip" onClick={() => window.print()}>Stampa</button>
                                            <button type="button" className="sid-chip is-arancio"
                                                onClick={() => setDaRimuovere(selezionati)}>
                                                Rimuovi dal corso
                                            </button>
                                        </div>
                                    )}
                                    {selezionati.length > 0 && avvisoAssegna && (
                                        <Alert variant="danger" className="text-dark mt-2 mb-0" dismissible onClose={() => setAvvisoAssegna(null)}>
                                            <strong>{avvisoAssegna}</strong>
                                        </Alert>
                                    )}

                                    <div className="table-responsive mt-3">
                                        <table className="table dataTable display">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 40 }}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            aria-label="Seleziona tutti"
                                                            checked={pagineDi.length > 0 && pagineDi.every(p => selezionati.includes(p.id))}
                                                            onChange={(e) => setSelezionati(e.target.checked ? pagineDi.map(p => p.id) : [])}
                                                        />
                                                    </th>
                                                    <th>Nome</th>
                                                    <th>Email</th>
                                                    <th>Telefono</th>
                                                    <th>Pagamento</th>
                                                    <th>Squadra</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagineDi.map(p => {
                                                    const squadra = c.squadre.find(s => s.id === p.squadraId);
                                                    return (
                                                        <tr key={p.id} className="sid-row-link" role="button" tabIndex={0}
                                                            onClick={() => spunta(p.id)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') spunta(p.id); }}>
                                                            <td onClick={(e) => e.stopPropagation()}>
                                                                <Form.Check type="checkbox" aria-label={p.nome} checked={selezionati.includes(p.id)} onChange={() => spunta(p.id)} />
                                                            </td>
                                                            <td className="sid-row-link__name">{p.nome}</td>
                                                            <td>{p.email}</td>
                                                            <td>{p.telefono}</td>
                                                            <td>
                                                                <PagamentoAcquisto forma={p.acquisto} pagato={p.pagato} />
                                                            </td>
                                                            <td>
                                                                {squadra
                                                                    ? <span className="sid-chip-squadra">{squadra.nome}</span>
                                                                    : <span className="sid-chip-attesa">Da assegnare</span>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {partecipantiFiltrati.length === 0 && (
                                                    <tr><td colSpan={6} className="text-center text-muted py-4">Nessun partecipante in questo elenco.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-between footTab">
                                        <span className="sid-list-total">
                                            {partecipantiFiltrati.length === 1 ? '1 partecipante' : `${partecipantiFiltrati.length} partecipanti`}
                                        </span>
                                        <SidPagination
                                            pageIndex={Math.min(pagina, pagine - 1)}
                                            pageCount={pagine}
                                            gotoPage={setPagina}
                                            previousPage={() => setPagina(p => Math.max(0, p - 1))}
                                            nextPage={() => setPagina(p => Math.min(pagine - 1, p + 1))}
                                            canPreviousPage={pagina > 0}
                                            canNextPage={pagina < pagine - 1}
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                    </Tab.Content>
                </Tab.Container>
            </div>

            {salvato && <Alert variant="success" className="text-dark mt-4 mb-0">Dati del corso salvati.</Alert>}

            <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                <Button variant="danger" className="light" onClick={() => setCorsoDaEliminare(true)}>Elimina corso</Button>
                <div className="d-flex gap-4">
                <button type="button" className="btn btn-dark light" onClick={() => navigate('/struttura/corsi')}>Indietro</button>
                {tabAttivo === 'informazioni' && (
                    <Button className="btn btn-primary" onClick={async () => {
                        const esito = await conAttesa('Salvataggio del corso…', async () => {
                            const problema = await salvaCorso(datiCorso || c, c.stato);
                            return problema ? { errore: true, testo: `Salvataggio non riuscito: ${frase(problema)}` } : null;
                        });
                        if (!esito) { setDatiCorso(null); setSalvato(true); }
                    }}>Salva</Button>
                )}
                </div>
            </div>
        </>
    );
};

/** Scheda «Informazioni generali»: gli stessi dati che il corso chiede oggi. */
const InformazioniCorso = ({ dati, onCambia }) => {
    const [caricaFoto, setCaricaFoto] = useState(false);
    const cambia = (modifiche) => onCambia({ ...dati, ...modifiche });
    const campo = (chiave, etichetta, tipo = 'text') => (
        <>
            <Form.Label className="mb-2">{etichetta}</Form.Label>
            <Form.Control className="mt-2" type={tipo === 'prezzo' ? 'text' : tipo} inputMode={tipo === 'prezzo' ? 'decimal' : undefined}
                value={dati[chiave] ?? ''} onChange={(e) => cambia({ [chiave]: e.target.value })} />
        </>
    );
    const immagine = dati.nuovaFoto ? `data:image/jpeg;base64,${dati.nuovaFoto}` : dati.foto;
    const interruttore = (chiave, etichetta) => (
        <div className="sid-switch-row">
            <Form.Check type="switch" id={`sw-${chiave}`} label={etichetta} checked={!!dati[chiave]} onChange={(e) => cambia({ [chiave]: e.target.checked })} />
        </div>
    );

    return (
        <Card>
            <Card.Body className="sid-card-body--spaced">
                <Row className="g-4">
                    <Col md={6}>{campo('nome', 'Nome corso')}</Col>
                    <Col md={2}>
                        <Form.Label className="mb-2">Tipologia di sport</Form.Label>
                        <Select className="mt-2" styles={sidSelectStyles} placeholder=""
                            value={sports.filter(s => s.value === dati.sportType).map(s => ({ value: s.value, label: s.label }))[0] || null}
                            options={sports.map(s => ({ value: s.value, label: s.label }))}
                            onChange={(o) => cambia({ sportType: o.value, sport: o.label })} />
                    </Col>
                    <Col md={2}>{campo('maxPartecipanti', 'Partecipanti massimi', 'number')}</Col>
                    {dati.stato === 'Bozza' && <Col md={2}>
                        <Form.Label className="mb-2">Stato</Form.Label>
                        {/* Lo stato Attivo / Non attivo sta nella chip accanto al titolo; qui resta solo per
                            pubblicare una bozza. Una bozza si pubblica; un corso pubblicato si accende e si spegne. Tornare
                            in bozza il server non lo permette, quindi non lo si propone nemmeno. */}
                        <Select className="mt-2" styles={sidSelectStyles}
                            value={{ value: dati.stato, label: dati.stato }}
                            options={(dati.stato === 'Bozza' ? ['Bozza', 'Attivo'] : ['Attivo', 'Non attivo'])
                                .map(x => ({ value: x, label: x }))}
                            onChange={(o) => cambia({ stato: o.value })} />
                    </Col>}
                    <Col md={12}>
                        <Form.Label className="mb-2">Immagine del corso</Form.Label>
                        <div className="mt-2 d-flex flex-wrap align-items-end gap-3">
                            {immagine
                                ? <img src={immagine} alt="" style={{ maxHeight: 160, maxWidth: '100%', borderRadius: 7 }} />
                                : <span className="text-muted">Nessuna immagine caricata</span>}
                            <Button className="btn btn-primary btn-sm" onClick={() => setCaricaFoto(true)}>
                                {immagine ? 'Sostituisci immagine' : 'Carica immagine'}
                            </Button>
                        </div>
                        <ProductImageUploader
                            show={caricaFoto}
                            onClose={() => setCaricaFoto(false)}
                            onSave={(base64) => { setCaricaFoto(false); cambia({ nuovaFoto: base64 }); }}
                            titolo="Immagine del corso"
                        />
                    </Col>
                    <Col md={12}>
                        <Form.Label className="mb-2">Descrizione</Form.Label>
                        <div className="mt-2">
                            <CKEditor
                                editor={ClassicEditor}
                                data={dati.descrizione || ''}
                                onChange={(evento, editor) => cambia({ descrizione: editor.getData() })}
                            />
                        </div>
                    </Col>
                </Row>

                <h5 className="sid-form-section">Prezzi</h5>
                <Row className="g-4">
                    <Col md={3}>{campo('prezzoStandard', 'Prezzo standard', 'prezzo')}</Col>
                    <Col md={3}>
                        <Form.Label className="mb-2">IVA</Form.Label>
                        <Select className="mt-2" styles={sidSelectStyles}
                            value={findVatOption(dati)}
                            options={VAT_OPTIONS}
                            onChange={(o) => cambia(vatFieldsFromOption(o))} />
                    </Col>
                    <Col md={3}>{campo('prezzoTesserati', 'Prezzo tesserati / soci', 'prezzo')}</Col>
                    <Col md={3}>{campo('cashback', 'Cashback Fitcoin', 'number')}</Col>
                </Row>

                <h5 className="sid-form-section">Forme di pagamento</h5>
                <div className="sid-switch-inline">
                    {interruttore('pagaInStruttura', 'Acquisto pagabile in struttura')}
                    {interruttore('pagaStripe', 'Acquisto pagabile su Stripe')}
                    {interruttore('pagaBonifico', 'Acquisto pagabile con bonifico')}
                </div>

            </Card.Body>
        </Card>
    );
};

/** Modifica di una singola data della pianificazione, nel pannello sotto la riga. */
const ModificaData = ({ data, altre, maestri, impianti, nomi, esistente, onCambia, onSalva, onAnnulla, onElimina }) => {
    const cambia = (modifiche) => onCambia({ ...data, ...modifiche });
    const orarioValido = data.data && data.inizio && data.fine && data.fine > data.inizio;
    // Il conflitto si vede mentre si modifica, e finché c'è non si salva
    const conflitto = orarioValido ? conflittoDi(data, altre, nomi) : null;
    const valida = orarioValido && !conflitto;
    return (
        <div className="sid-occ-panel">
            <Row className="g-3 align-items-end">
                <Col md={2}>
                    <Form.Label className="mb-1 small">Data</Form.Label>
                    <Form.Control type="date" value={data.data} onChange={(e) => cambia({ data: e.target.value })} />
                </Col>
                <Col md={2}>
                    <Form.Label className="mb-1 small">Ora inizio</Form.Label>
                    <Form.Control type="time" value={data.inizio} onChange={(e) => cambia({ inizio: e.target.value })} />
                </Col>
                <Col md={2}>
                    <Form.Label className="mb-1 small">Ora fine</Form.Label>
                    <Form.Control type="time" value={data.fine} onChange={(e) => cambia({ fine: e.target.value })} />
                </Col>
                <Col md={3}>
                    <Form.Label className="mb-1 small">Maestro</Form.Label>
                    <Select styles={sidSelectStyles} placeholder=""
                        value={maestri.filter(m => m.id === data.maestroId).map(m => ({ value: m.id, label: m.nome }))[0] || null}
                        options={maestri.map(m => ({ value: m.id, label: m.nome }))}
                        onChange={(o) => cambia({ maestroId: o?.value || '' })} />
                </Col>
                <Col md={3}>
                    <Form.Label className="mb-1 small">Impianto</Form.Label>
                    <Select styles={sidSelectStyles} placeholder=""
                        value={impianti.filter(v => v.id === data.impiantoId).map(v => ({ value: v.id, label: v.nome }))[0] || null}
                        options={impianti.map(v => ({ value: v.id, label: v.nome }))}
                        onChange={(o) => cambia({ impiantoId: o?.value || '' })} />
                </Col>
            </Row>
            {!orarioValido && <div className="text-danger mt-2" style={{ fontSize: 13 }}>L&apos;ora di fine deve venire dopo l&apos;ora di inizio.</div>}
            {conflitto && (
                <div className="text-danger mt-2" style={{ fontSize: 13 }}>
                    <span className="sid-chip-attesa me-2">Conflitto</span>{conflitto}. Cambia orario, maestro o impianto per poter salvare.
                </div>
            )}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <Button variant="outline-danger" className="sid-btn-compact" onClick={onElimina}>Elimina questa data</Button>
                <div className="d-flex flex-wrap gap-2">
                <Button variant="dark" className="light sid-btn-compact" onClick={onAnnulla}>Annulla</Button>
                <Button variant="outline-dark" className="sid-btn-compact" disabled={!valida} onClick={() => onSalva(false)}>Salva solo questa data</Button>
                <Button
                    variant="primary"
                    className="sid-btn-compact"
                    disabled={!valida}
                    title={esistente
                        ? 'Da questa data in poi le sedute vengono rifatte con questo orario, maestro e impianto'
                        : undefined}
                    onClick={() => onSalva(true)}>
                    Salva e aggiorna da qui in poi
                </Button>
                </div>
            </div>
        </div>
    );
};

/** Stato della squadra: verdino se attiva, rossiccio se non attiva, neutro se bozza. */
const ChipStato = ({ stato }) => (
    <span className={stato === 'Attiva' ? 'sid-chip-ok' : stato === 'Non attiva' ? 'sid-chip-ko' : 'sid-chip-squadra'}>{stato}</span>
);

const Riquadro = ({ etichetta, valore, nota, allarme, allarmeNota }) => (
    <Card className="mb-0">
        <Card.Body className="py-3">
            <div className="text-muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>{etichetta}</div>
            <div className={`fw-bold ${allarme ? 'text-danger' : ''}`} style={{ fontSize: 24 }}>{valore}</div>
            {nota && <div className={allarmeNota ? 'text-danger fw-bold' : 'text-muted'} style={{ fontSize: 12 }}>{nota}</div>}
        </Card.Body>
    </Card>
);

/**
 * Modifica di una squadra, dentro la scheda del corso. I dati su una riga sola; poi la
 * pianificazione: una riga generatrice (giorno, orario, maestro, impianto) che con «Genera» crea
 * tutte le date fra il primo e l'ultimo giorno della squadra.
 *
 * Finché la squadra non esiste le date si compongono qui e partono tutte insieme alla creazione.
 * Quando la squadra c'è, ogni riga è una seduta vera a calendario: spostarla, eliminarla o rifare
 * da lì in poi sono tre operazioni che vanno subito sul gestionale.
 */
const PannelloSquadra = ({ squadra, dateAltreSquadre, esistente, maxCorso, sport, corsoAttivo = true, maestri: tuttiIMaestri, impianti: tuttiGliImpianti, nomi, onCambia, onSalva, onAnnulla, onDuplica, onElimina, conAttesa, onStatoCambiato }) => {
    // Piu' posti di quanti ne ammette il corso: il gestionale rifiuta, quindi non si parte
    const postiOltreIlCorso = maxCorso > 0 && (parseInt(squadra.maxMembri, 10) || 0) > maxCorso;
    /*
     * Il discrimine di tutta la pianificazione non e' se la squadra esiste, ma se le sue
     * sedute sono gia' a calendario: succede quando la si attiva. Da li' in poi spostare,
     * eliminare o aggiungere una data e' una scrittura sul gestionale, e il periodo si
     * cambia solo dal riallineamento. Finche' e' bozza si sta ancora decidendo: si lavora
     * in locale e si salva tutto insieme.
     */
    const aCalendario = esistente && squadra.stato !== 'Bozza';
    const periodoBloccato = aCalendario;
    const [regola, setRegola] = useState({ giorno: '', inizio: '17:00', fine: '18:00', maestroId: '', impiantoId: '' });
    const [avvisoRegola, setAvvisoRegola] = useState(null);
    const [pagina, setPagina] = useState(0);
    const [dataAperta, setDataAperta] = useState(null); // copia di lavoro della data in modifica
    const [dateScelte, setDateScelte] = useState([]);
    const date = squadra.date || [];

    // Si propongono solo maestri e impianti dello sport del corso: gli altri il server li rifiuta.
    const maestri = sport ? tuttiIMaestri.filter(m => m.sport === sport) : tuttiIMaestri;
    const impianti = sport ? tuttiGliImpianti.filter(v => !v.sport || v.sport === sport) : tuttiGliImpianti;

    /*
     * Finché la squadra è in bozza le date vivono qui, e il controllo locale vede solo le
     * sovrapposizioni fra le date del corso. Orari di apertura dell'impianto e disponibilità
     * del maestro li conosce il server: glieli si chiede, così un «nessun conflitto» qui è
     * un'attivazione che poi riesce.
     */
    const [motiviServer, setMotiviServer] = useState({});
    const [controlloServer, setControlloServer] = useState(null); // null | 'in corso' | messaggio d'errore
    const firmaDate = date.map(d => `${d.data}-${d.inizio}-${d.fine}-${d.maestroId}-${d.impiantoId}`).join('|');
    useEffect(() => {
        if (aCalendario || date.length === 0) { setMotiviServer({}); setControlloServer(null); return undefined; }
        let annullato = false;
        setControlloServer('in corso');
        const attesa = setTimeout(async () => {
            const esito = await controllaDateSulServer(date, sport);
            if (annullato) return;
            setMotiviServer(esito.motivi);
            setControlloServer(esito.errore ? `Controllo di impianto e maestro non riuscito: ${esito.errore}` : null);
        }, 600);
        return () => { annullato = true; clearTimeout(attesa); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firmaDate, aCalendario, sport]);

    const cambia = (modifiche) => onCambia({ ...squadra, ...modifiche });

    /**
     * Salva la data modificata. Con «da qui in poi» la modifica vale anche per le date successive
     * dello stesso giorno della settimana: è il caso vero di una stagione, quando da un certo
     * punto l'allenamento cambia ora, campo o maestro.
     */
    const salvaData = async (modificata, daQuiInPoi) => {
        if (!aCalendario) {
            const originale = date.find(x => x.id === modificata.id);
            const nuove = date.map(x => {
                if (x.id === modificata.id) return modificata;
                if (!daQuiInPoi || !originale) return x;
                const dopo = x.data > originale.data;
                const stessoSchema = x.giorno === originale.giorno && x.inizio === originale.inizio && x.fine === originale.fine
                    && x.maestroId === originale.maestroId && x.impiantoId === originale.impiantoId;
                if (!dopo || !stessoSchema) return x;
                return { ...x, inizio: modificata.inizio, fine: modificata.fine, maestroId: modificata.maestroId, impiantoId: modificata.impiantoId };
            });
            cambia({ date: nuove.sort((a, b) => (a.data + a.inizio).localeCompare(b.data + b.inizio)) });
            setDataAperta(null);
            return;
        }

        setDataAperta(null);
        if (daQuiInPoi) {
            await conAttesa('Rifaccio le sedute da questa data…', async () => {
                const esito = await riallineaDaSeduta(squadra, modificata);
                if (esito?.errore) return { errore: true, testo: `Non si è potuto rifare: ${frase(esito.errore)}` };
                return {
                    testo: `Sedute rifatte dal ${dataBreve(modificata.data)}: ${esito.created} nuove, `
                        + `${esito.removed} tolte, ${esito.kept} lasciate come stavano.`,
                };
            });
            return;
        }
        await conAttesa('Sposto la seduta…', async () => {
            const problema = await spostaSeduta(modificata);
            return problema ? { errore: true, testo: `Spostamento non riuscito: ${frase(problema)}` } : null;
        });
    };

    const eliminaData = async (d) => {
        setDataAperta(null);
        if (!aCalendario) { cambia({ date: date.filter(x => x.id !== d.id) }); return; }
        await conAttesa('Elimino la seduta…', async () => {
            const problema = await eliminaSeduta(squadra.id, d.id);
            return problema ? { errore: true, testo: `Eliminazione non riuscita: ${frase(problema)}` } : null;
        });
    };

    const eliminaScelte = async () => {
        const scelte = date.filter(x => dateScelte.includes(x.id));
        setDateScelte([]);
        setDataAperta(null);
        if (!aCalendario) { cambia({ date: date.filter(x => !scelte.some(s => s.id === x.id)) }); return; }
        await conAttesa(`Elimino ${scelte.length} sedute…`, async (passo) => {
            const problemi = [];
            for (let i = 0; i < scelte.length; i++) {
                passo(`Elimino la seduta ${i + 1} di ${scelte.length}…`);
                const problema = await eliminaSeduta(squadra.id, scelte[i].id);
                if (problema) problemi.push(`${scelte[i].data}: ${problema}`);
            }
            return problemi.length > 0 ? { errore: true, testo: `Qualcosa non è andato: ${problemi.join('; ')}.` } : null;
        });
    };

    const genera = async () => {
        if (!squadra.dal || !squadra.al) return setAvvisoRegola('Indica prima il periodo della squadra (dal e al).');
        if (!regola.giorno) return setAvvisoRegola('Scegli il giorno della settimana.');
        if (!regola.inizio || !regola.fine || regola.fine <= regola.inizio) return setAvvisoRegola("L'ora di fine deve venire dopo l'ora di inizio.");
        if (!regola.maestroId) return setAvvisoRegola('Scegli il maestro.');
        if (!regola.impiantoId) return setAvvisoRegola("Scegli l'impianto.");

        const nuove = generaDateRegola(regola, squadra.dal, squadra.al);
        if (nuove.length === 0) return setAvvisoRegola('Nessuna data: in quel periodo non cade mai questo giorno della settimana.');

        // Lo stesso giorno e orario non si aggiunge due volte
        const giaPresenti = new Set(date.map(d => `${d.data}-${d.inizio}-${d.fine}`));
        const daAggiungere = nuove.filter(d => !giaPresenti.has(`${d.data}-${d.inizio}-${d.fine}`));
        setPagina(0);

        if (!aCalendario) {
            cambia({ date: [...date, ...daAggiungere].sort((a, b) => (a.data + a.inizio).localeCompare(b.data + b.inizio)) });
            setAvvisoRegola(daAggiungere.length === nuove.length
                ? `${daAggiungere.length} date aggiunte.`
                : `${daAggiungere.length} date aggiunte, ${nuove.length - daAggiungere.length} c'erano già.`);
            return;
        }

        if (daAggiungere.length === 0) return setAvvisoRegola('Quelle date ci sono già tutte.');
        setAvvisoRegola(null);
        await conAttesa(`Aggiungo ${daAggiungere.length} sedute…`, async (passo) => {
            const esito = await aggiungiSedute(squadra.id, daAggiungere, passo);
            return {
                errore: esito.problemi.length > 0,
                testo: `${esito.aggiunte} sedute aggiunte`
                    + (esito.problemi.length > 0 ? `, ${esito.problemi.length} no: ${esito.problemi.join('; ')}.` : '.'),
            };
        });
    };

    const tutte = [...date.map(d => ({ data: d, squadra: null })), ...dateAltreSquadre];
    const conConflitto = date.map(d => ({ ...d, conflitto: conflittoDi(d, tutte, nomi) || motiviServer[`${d.data}-${d.inizio}-${d.fine}`] || null }));
    const quantiConflitti = conConflitto.filter(d => d.conflitto).length;
    const PER_PAGINA = 12;
    const pagine = Math.max(1, Math.ceil(conConflitto.length / PER_PAGINA));
    const paginaCorrente = Math.min(pagina, pagine - 1);
    const dellaPagina = conConflitto.slice(paginaCorrente * PER_PAGINA, (paginaCorrente + 1) * PER_PAGINA);

    return (
        <Card className="mb-0">
            <Card.Body className="sid-card-body--spaced">
                <Row className="g-4">
                    <Col md={4}>
                        <Form.Label className="mb-2">Nome squadra</Form.Label>
                        <Form.Control className="mt-2" value={squadra.nome} onChange={(e) => cambia({ nome: e.target.value })} />
                    </Col>
                    <Col md={2}>
                        <Form.Label className="mb-2">Dal</Form.Label>
                        <Form.Control className="mt-2" type="date" value={squadra.dal || ''} disabled={periodoBloccato}
                            title={periodoBloccato ? 'Il periodo di una squadra attiva si cambia dalla pianificazione, una seduta alla volta' : undefined}
                            onChange={(e) => cambia({ dal: e.target.value })} />
                    </Col>
                    <Col md={2}>
                        <Form.Label className="mb-2">Al</Form.Label>
                        <Form.Control className="mt-2" type="date" value={squadra.al || ''} disabled={periodoBloccato}
                            title={periodoBloccato ? 'Il periodo di una squadra attiva si cambia dalla pianificazione, una seduta alla volta' : undefined}
                            onChange={(e) => cambia({ al: e.target.value })} />
                    </Col>
                    <Col md={2}>
                        <Form.Label className="mb-2">Posti</Form.Label>
                        <Form.Control className="mt-2" type="number" min="1" max={maxCorso || undefined}
                            value={squadra.maxMembri} onChange={(e) => cambia({ maxMembri: e.target.value })} />
                        {postiOltreIlCorso && (
                            <div className="text-danger small mt-1">Il corso ne ammette {maxCorso}.</div>
                        )}
                    </Col>
                    <Col md={2}>
                        <Form.Label className="mb-2">Stato</Form.Label>
                        {aCalendario ? (
                            /* Squadra già a calendario: Attiva / Non attiva si salva subito (regola SUI) */
                            <div className="mt-2">
                                <SelettoreStato
                                    stati={[
                                        { value: 'Attiva', label: 'Attiva', tono: 'attivo' },
                                        { value: 'Non attiva', label: 'Non attiva', tono: 'non-attivo' },
                                    ]}
                                    valore={squadra.stato}
                                    onCambia={async (v) => {
                                        const problema = await cambiaStatoSquadra(squadra.id, v);
                                        if (problema) return { error: problema };
                                        cambia({ stato: v });
                                        if (onStatoCambiato) await onStatoCambiato();
                                        return {};
                                    }}
                                />
                            </div>
                        ) : (
                            /* Bozza o squadra nuova: lo stato decide se il salvataggio la attiva */
                            <Select className="mt-2" styles={sidSelectStyles}
                                value={{ value: squadra.stato, label: squadra.stato }}
                                options={STATI_SQUADRA.map(x => ({ value: x, label: x }))}
                                onChange={(o) => cambia({ stato: o.value })} />
                        )}
                    </Col>
                </Row>

                <h5 className="sid-form-section">Pianificazione</h5>
                <div className="sid-gen-panel">
                    <div className="sid-gen-panel__titolo">Genera le date</div>
                    <p className="sid-gen-panel__aiuto">
                        Scegli un giorno della settimana con il suo orario, il maestro e l&apos;impianto: premendo «Genera» nascono tutte le
                        date fra il {dataBreve(squadra.dal)} e il {dataBreve(squadra.al)}. Ripeti per ogni giorno di allenamento.
                        {aCalendario && ' La squadra è attiva: le date generate vanno subito a calendario.'}
                    </p>
                    <Row className="g-3 align-items-end">
                        <Col md={2}>
                            <Form.Label className="mb-1 small">Giorno</Form.Label>
                        <Select styles={sidSelectStyles} placeholder=""
                            value={GIORNI.filter(g => g.value === regola.giorno).map(g => ({ value: g.value, label: g.label }))[0] || null}
                            options={GIORNI.map(g => ({ value: g.value, label: g.label }))}
                            onChange={(o) => { setRegola({ ...regola, giorno: o?.value || '' }); setAvvisoRegola(null); }} />
                    </Col>
                        <Col md={2}>
                            <Form.Label className="mb-1 small">Ora inizio</Form.Label>
                        <Form.Control type="time" value={regola.inizio} onChange={(e) => { setRegola({ ...regola, inizio: e.target.value }); setAvvisoRegola(null); }} />
                    </Col>
                        <Col md={2}>
                            <Form.Label className="mb-1 small">Ora fine</Form.Label>
                        <Form.Control type="time" value={regola.fine} onChange={(e) => { setRegola({ ...regola, fine: e.target.value }); setAvvisoRegola(null); }} />
                    </Col>
                        <Col md={2}>
                            <Form.Label className="mb-1 small">Maestro</Form.Label>
                        <Select styles={sidSelectStyles} placeholder=""
                            value={maestri.filter(m => m.id === regola.maestroId).map(m => ({ value: m.id, label: m.nome }))[0] || null}
                            options={maestri.map(m => ({ value: m.id, label: m.nome }))}
                            onChange={(o) => { setRegola({ ...regola, maestroId: o?.value || '' }); setAvvisoRegola(null); }} />
                    </Col>
                        <Col md={2}>
                            <Form.Label className="mb-1 small">Impianto</Form.Label>
                        <Select styles={sidSelectStyles} placeholder=""
                            value={impianti.filter(v => v.id === regola.impiantoId).map(v => ({ value: v.id, label: v.nome }))[0] || null}
                            options={impianti.map(v => ({ value: v.id, label: v.nome }))}
                            onChange={(o) => { setRegola({ ...regola, impiantoId: o?.value || '' }); setAvvisoRegola(null); }} />
                    </Col>
                        <Col md={2}>
                            <Button variant="primary" size="sm" onClick={genera}>Genera</Button>
                        </Col>
                    </Row>
                    {avvisoRegola && <div className="sid-gen-panel__esito">{avvisoRegola}</div>}
                </div>

                {date.length > 0 && (
                    <>
                        {dateScelte.length > 0 && (
                            <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                                <span className="sid-list-total">{dateScelte.length === 1 ? '1 data scelta' : `${dateScelte.length} date scelte`}</span>
                                <button type="button" className="sid-chip" onClick={() => setDateScelte([])}>Deseleziona</button>
                                <button type="button" className="sid-chip" onClick={eliminaScelte}>
                                    Elimina le date scelte
                                </button>
                            </div>
                        )}
                        <div className="table-responsive mt-2">
                            <table className="sid-plan-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}>
                                            <Form.Check
                                                type="checkbox"
                                                aria-label="Scegli tutte le date della pagina"
                                                checked={dellaPagina.length > 0 && dellaPagina.every(d => dateScelte.includes(d.id))}
                                                onChange={(e) => setDateScelte(e.target.checked ? dellaPagina.map(d => d.id) : [])}
                                            />
                                        </th>
                                        <th>Data</th>
                                        <th>Giorno</th>
                                        <th>Orario</th>
                                        <th>Maestro</th>
                                        <th>Impianto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dellaPagina.map(d => (
                                        <React.Fragment key={d.id}>
                                        <tr className={`sid-row-link${dataAperta?.id === d.id ? ' is-open' : ''}`}
                                            role="button" tabIndex={0}
                                            onClick={() => setDataAperta(dataAperta?.id === d.id ? null : { ...d })}
                                            onKeyDown={(e) => { if (e.key === 'Enter') setDataAperta(dataAperta?.id === d.id ? null : { ...d }); }}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <Form.Check
                                                    type="checkbox"
                                                    aria-label={`Scegli il ${d.data}`}
                                                    checked={dateScelte.includes(d.id)}
                                                    onChange={() => setDateScelte(prec => (prec.includes(d.id) ? prec.filter(x => x !== d.id) : [...prec, d.id]))}
                                                />
                                            </td>
                                            <td>
                                                {new Date(d.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                {d.conflitto && <span className="sid-chip-attesa ms-2" title={d.conflitto}>Conflitto</span>}
                                            </td>
                                            <td>{nomeGiorno(d.giorno)}</td>
                                            <td>{d.inizio} - {d.fine}</td>
                                            <td>{nomi.maestro(d.maestroId)}</td>
                                            <td>{nomi.impianto(d.impiantoId)}</td>
                                        </tr>
                                        {dataAperta?.id === d.id && (
                                            <tr className="sid-plan-table__panel">
                                                <td colSpan={6} style={{ textAlign: 'left' }}>
                                                    <ModificaData
                                                        data={dataAperta}
                                                        altre={tutte}
                                                        maestri={maestri}
                                                        impianti={impianti}
                                                        nomi={nomi}
                                                        esistente={aCalendario}
                                                        onCambia={setDataAperta}
                                                        onAnnulla={() => setDataAperta(null)}
                                                        onElimina={() => eliminaData(dataAperta)}
                                                        onSalva={(daQui) => salvaData(dataAperta, daQui)}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex align-items-center justify-content-between footTab">
                            <span className="sid-list-total">
                                {date.length === 1 ? '1 data' : `${date.length} date`} dal {dataBreve(squadra.dal)} al {dataBreve(squadra.al)}
                                {controlloServer === 'in corso'
                                    ? ' · controllo di impianto e maestro…'
                                    : quantiConflitti > 0
                                        ? <span className="text-danger fw-bold"> · {quantiConflitti} in conflitto</span>
                                        : controlloServer
                                            ? <span className="text-danger"> · {controlloServer}</span>
                                            : ' · nessun conflitto'}
                            </span>
                            <SidPagination
                                pageIndex={paginaCorrente}
                                pageCount={pagine}
                                gotoPage={setPagina}
                                previousPage={() => setPagina(p => Math.max(0, p - 1))}
                                nextPage={() => setPagina(p => Math.min(pagine - 1, p + 1))}
                                canPreviousPage={paginaCorrente > 0}
                                canNextPage={paginaCorrente < pagine - 1}
                            />
                        </div>
                    </>
                )}

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-4 mt-4">
                    <div className="d-flex flex-wrap gap-2">
                        {esistente && (
                            <>
                                {corsoAttivo && <Button variant="outline-dark" size="sm" onClick={onDuplica}><Copy size="14" /> Duplica squadra</Button>}
                                <Button variant="outline-danger" size="sm" onClick={onElimina}><Trash size="14" /> Elimina squadra</Button>
                            </>
                        )}
                    </div>
                    <div className="d-flex flex-wrap gap-4">
                        <button type="button" className="btn btn-dark light" onClick={onAnnulla}>Annulla</button>
                        <Button className="btn btn-primary" onClick={onSalva} disabled={postiOltreIlCorso}
                            title={postiOltreIlCorso ? `Il corso ammette ${maxCorso} partecipanti` : undefined}>
                            {esistente ? 'Salva squadra' : 'Crea la squadra'}
                        </Button>
                    </div>
                </div>

            </Card.Body>
        </Card>
    );
};

export default CorsiCorsoPage;
